import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid2";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { contactV1Or2 } from "config/paths";

import { sectionSpacing } from "../home/section-spacing";
import { headingFontFamily } from "../heading-font";

const evolution = [
	{
		year: "2008",
		src: "/app-assets/images/mangomap-evolution-2008.png",
		caption:
			"Where it began: a turnkey internet GIS service, built on open source for clients who needed their data online.",
	},
	{
		year: "2009",
		src: "/app-assets/images/mangomap-evolution-2009.png",
		caption:
			"Upload a shapefile, style it, publish. The bespoke service becomes a self-serve product.",
	},
	{
		year: "2010",
		src: "/app-assets/images/mangomap-evolution-2010.png",
		caption:
			"“Create beautiful maps in minutes” — trusted by development agencies around the world.",
	},
	{
		year: "2013",
		src: "/app-assets/images/mangomap-evolution-2013.png",
		caption:
			"MangoMap launches as a platform from the UK — interactive web maps for everyone, no code required.",
	},
	{
		year: "2015",
		src: "/app-assets/images/mangomap-evolution-2015.png",
		caption:
			"Refined for the people who rely on it: local government, demographics, land, and infrastructure.",
	},
	{
		year: "2018",
		src: "/app-assets/images/mangomap-evolution-2018.png",
		caption:
			"The simple online GIS — portal-driven and industry-ready, still unmistakably Mango.",
	},
] as const;

/** A period screenshot dressed as a preserved browser window. */
function BrowserFrame({ src, alt }: { src: string; alt: string }) {
	return (
		<Box
			sx={{
				border: "1px solid var(--mui-palette-divider)",
				borderRadius: 3,
				boxShadow: "var(--mui-shadows-8)",
				overflow: "hidden",
				width: "100%",
			}}>
			<Stack
				direction="row"
				spacing={0.75}
				sx={{
					alignItems: "center",
					bgcolor: "var(--mui-palette-neutral-100)",
					borderBottom: "1px solid var(--mui-palette-divider)",
					px: 1.5,
					py: 1,
				}}>
				{[0, 1, 2].map((dot) => (
					<Box
						key={dot}
						sx={{
							bgcolor: "var(--mui-palette-neutral-300)",
							borderRadius: "50%",
							height: 9,
							width: 9,
						}}
					/>
				))}
			</Stack>
			<Box
				alt={alt}
				component="img"
				loading="lazy"
				src={src}
				sx={{ display: "block", height: "auto", width: "100%" }}
			/>
		</Box>
	);
}

export function About() {
	return (
		<Box sx={{ py: sectionSpacing }}>
			<Container maxWidth="md">
				<Stack spacing={2}>
					<Typography
						color="text.secondary"
						variant="overline">
						About
					</Typography>
					<Typography
						color="text.primary"
						component="h1"
						sx={{ fontFamily: headingFontFamily, fontWeight: 700 }}
						variant="h3">
						About MangoMap
					</Typography>
					<Typography
						color="text.primary"
						component="h2"
						variant="subtitle1">
						Mango makes creating engaging, data-driven web maps effortless — no
						coding, no servers, no complications.
					</Typography>
					<Typography>
						We didn&apos;t start as a software company. We started in consulting
						— building bespoke, turnkey web maps for organisations that needed
						their spatial data online, back when that meant running your own
						servers and writing your own tools.
					</Typography>
					<Typography>
						By 2013 we&apos;d built enough of them to see the pattern, so we
						turned the craft into a product and launched MangoMap as a
						self-service platform from the UK — the same capability, without the
						bespoke price tag or the wait.
					</Typography>
					<Typography>
						Today, our global community spans over 115 countries, with hundreds
						of thousands of maps created across a wide range of industries —
						local government, urban planning, environmental and conservation,
						public health and epidemiology, utilities and energy, engineering
						and infrastructure, surveying and field data collection, natural
						resources and agriculture, real estate and property, education and
						research, franchising and multi-location businesses, and business
						intelligence. At the heart of Mango is our dedicated team of
						experts, committed to delivering a seamless mapping experience that
						drives real results for your organization.
					</Typography>
					<Typography>
						Headquartered in Bristol 🇬🇧, Mango continues to transform how
						organizations visualize and share data. Interested in learning more?{" "}
						<Link href={contactV1Or2}>Get in touch today</Link>.
					</Typography>
				</Stack>
			</Container>

			<Container
				maxWidth="lg"
				sx={{ mt: sectionSpacing }}>
				<Stack
					spacing={2}
					sx={{ maxWidth: 640, mb: { xs: 6, md: 8 } }}>
					<Typography
						color="text.secondary"
						variant="overline">
						Our heritage
					</Typography>
					<Typography
						color="text.primary"
						sx={{ fontFamily: headingFontFamily, fontWeight: 700 }}
						variant="h4">
						The evolution of Mango
					</Typography>
					<Typography color="text.secondary">
						We were putting maps on the web before it was a category.
						Here&apos;s how the product has changed over the years — and
						what&apos;s stayed the same.
					</Typography>
				</Stack>

				<Stack spacing={{ xs: 8, md: 12 }}>
					{evolution.map((item, index) => (
						<Grid
							key={item.year}
							container
							spacing={{ xs: 3, md: 6 }}
							sx={{ alignItems: "center" }}
							direction={{
								xs: "column",
								md: index % 2 === 0 ? "row" : "row-reverse",
							}}>
							<Grid size={{ xs: 12, md: 7 }}>
								<BrowserFrame
									alt={`MangoMap in ${item.year}`}
									src={item.src}
								/>
							</Grid>
							<Grid size={{ xs: 12, md: 5 }}>
								<Stack spacing={1.5}>
									<Typography
										color="secondary"
										sx={{
											fontFamily: headingFontFamily,
											fontWeight: 700,
											lineHeight: 1,
										}}
										variant="h2">
										{item.year}
									</Typography>
									<Typography
										color="text.secondary"
										variant="body1">
										{item.caption}
									</Typography>
								</Stack>
							</Grid>
						</Grid>
					))}
				</Stack>
			</Container>
		</Box>
	);
}
